/**
 * FieldsStep - Step 3: Select and order visible fields
 *
 * Features:
 * - Left panel: Available fields grouped by table (focus table + joined tables)
 * - Right panel: Selected fields with drag-and-drop reordering
 * - Per-field configuration (display label, visible, editable, is_link_field)
 * - System columns filtered from joined tables
 */

import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Checkbox,
  IconButton,
  Chip,
  Alert,
  Input,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionGroup,
  Sheet,
  CircularProgress,
} from "@mui/joy";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LinkIcon from "@mui/icons-material/Link";
import PersonPinIcon from "@mui/icons-material/PersonPin";
import JoinInnerIcon from "@mui/icons-material/JoinInner";
import TableChartIcon from "@mui/icons-material/TableChart";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TuneIcon from "@mui/icons-material/Tune";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { useViewBuilderContext } from "../ViewBuilderContext";
import { isSystemColumn } from "../../../types";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import type { SchemaColumnResponse } from "../../../types";

interface FieldsStepProps {
  workspaceId: string;
}

// Width hint options for column display
const WIDTH_OPTIONS = [
  { value: undefined, label: "Auto" },
  { value: 80, label: "Small (80px)" },
  { value: 120, label: "Medium (120px)" },
  { value: 180, label: "Large (180px)" },
  { value: 250, label: "Extra Large (250px)" },
] as const;

// Format type options by data type category
const FORMAT_OPTIONS: Record<
  string,
  Array<{ value: string | undefined; label: string }>
> = {
  number: [
    { value: undefined, label: "Default" },
    { value: "number", label: "Number (1,234)" },
    { value: "currency", label: "Currency ($1,234)" },
    { value: "percent", label: "Percent (12%)" },
    { value: "decimal2", label: "2 Decimals (1,234.56)" },
  ],
  date: [
    { value: undefined, label: "Default" },
    { value: "date-short", label: "Short (12/31/24)" },
    { value: "date-long", label: "Long (Dec 31, 2024)" },
    { value: "date-iso", label: "ISO (2024-12-31)" },
  ],
  datetime: [
    { value: undefined, label: "Default" },
    { value: "datetime-short", label: "Short (12/31/24 3:45 PM)" },
    { value: "datetime-long", label: "Long (Dec 31, 2024 3:45 PM)" },
    { value: "datetime-iso", label: "ISO (2024-12-31T15:45:00)" },
  ],
  text: [
    { value: undefined, label: "Default" },
    { value: "truncate", label: "Truncate (50 chars)" },
    { value: "link", label: "Clickable Link" },
    { value: "email", label: "Email Link" },
  ],
};

// Get format options based on data type
function getFormatOptionsForType(
  dataType: string,
): Array<{ value: string | undefined; label: string }> {
  const type = dataType.toUpperCase();

  if (
    type.includes("INT") ||
    type.includes("DECIMAL") ||
    type.includes("NUMERIC") ||
    type.includes("FLOAT") ||
    type.includes("REAL") ||
    type.includes("MONEY")
  ) {
    return FORMAT_OPTIONS.number;
  }
  if (type.includes("DATETIME") || type.includes("TIMESTAMP")) {
    return FORMAT_OPTIONS.datetime;
  }
  if (type.includes("DATE")) {
    return FORMAT_OPTIONS.date;
  }
  if (type.includes("CHAR") || type.includes("TEXT")) {
    return FORMAT_OPTIONS.text;
  }

  return [{ value: undefined, label: "Default" }];
}

/**
 * Convert SQL Server data types to user-friendly labels
 */
function getUserFriendlyType(dataType: string): string {
  const type = dataType.toUpperCase();

  if (type.includes("CHAR") || type.includes("TEXT")) return "Text";
  if (
    type.includes("INT") ||
    type.includes("DECIMAL") ||
    type.includes("NUMERIC") ||
    type.includes("FLOAT") ||
    type.includes("REAL") ||
    type.includes("MONEY")
  )
    return "Number";
  if (type.includes("DATE") || type.includes("TIME")) return "Date";
  if (type === "BIT") return "Yes/No";
  if (type.includes("UNIQUEIDENTIFIER")) return "ID";
  if (type.includes("BINARY") || type.includes("IMAGE")) return "File";

  return "Text"; // Default fallback
}

interface AvailableField {
  column: SchemaColumnResponse;
  tableId: string;
  tableName: string;
  tableAlias: string | null;
  isSelected: boolean;
}

interface TableFieldGroup {
  tableId: string;
  tableName: string;
  tableAlias: string | null;
  isFocusTable: boolean;
  fields: AvailableField[];
}

// Interface for combined link field groups in selected fields
interface CombinedLinkFieldGroup {
  tableAlias: string;
  tableName: string;
  fieldIds: string[];
  combinedLabel: string;
}

export default function FieldsStep({ workspaceId }: FieldsStepProps) {
  const {
    state,
    toggleField,
    reorderFields,
    updateField,
    isFieldSelected,
    getFieldError,
  } = useViewBuilderContext();

  // Get validation error for selected fields
  const fieldsError = getFieldError("selectedFields");

  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [settingsExpandedId, setSettingsExpandedId] = useState<string | null>(
    null,
  );

  // State to store columns for joined tables: { [tableId]: SchemaColumnResponse[] }
  const [joinedTableColumns, setJoinedTableColumns] = useState<
    Record<string, SchemaColumnResponse[]>
  >({});
  const [loadingJoinedColumns, setLoadingJoinedColumns] = useState(false);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  // Fetch columns for all joined tables
  const fetchJoinedTableColumns = useCallback(async () => {
    if (!workspaceId || state.selectedJoins.length === 0) {
      setJoinedTableColumns({});
      return;
    }

    // Get unique table IDs that we need to fetch columns for
    const tableIds = [
      ...new Set(state.selectedJoins.map((j) => j.targetTableId)),
    ];

    // Filter out tables we already have columns for
    const tableIdsToFetch = tableIds.filter((id) => !joinedTableColumns[id]);

    if (tableIdsToFetch.length === 0) return;

    setLoadingJoinedColumns(true);
    try {
      const results: Record<string, SchemaColumnResponse[]> = {
        ...joinedTableColumns,
      };

      // Fetch columns for each table in parallel
      await Promise.all(
        tableIdsToFetch.map(async (tableId) => {
          try {
            const columns = await authFetchRef.current(
              `/api/workspace/myvault/schemas/tables/${tableId}/columns`,
              "fetch joined table columns",
              { headers: { "x-workspace-id": workspaceId } },
            );
            results[tableId] = columns || [];
          } catch (err) {
            console.error(`Failed to fetch columns for table ${tableId}:`, err);
            results[tableId] = [];
          }
        }),
      );

      setJoinedTableColumns(results);
    } catch (err) {
      console.error("Error fetching joined table columns:", err);
    } finally {
      setLoadingJoinedColumns(false);
    }
  }, [workspaceId, state.selectedJoins, joinedTableColumns]);

  // Fetch columns when joins change
  useEffect(() => {
    fetchJoinedTableColumns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedJoins.length]); // Only re-fetch when number of joins changes

  // Build available field groups
  const fieldGroups = useMemo((): TableFieldGroup[] => {
    const groups: TableFieldGroup[] = [];

    // Focus table fields - filter out PK and FK columns (they're just IDs, not useful to display)
    if (state.focusTable && state.focusTableId) {
      const focusFields: AvailableField[] = (state.focusTable.columns || [])
        .filter((col) => !col.is_primary_key && !col.is_foreign_key)
        .map((col) => ({
          column: col,
          tableId: state.focusTableId!,
          tableName:
            state.focusTable!.display_name || state.focusTable!.table_name,
          tableAlias: null,
          isSelected: isFieldSelected(col.id, null),
        }));

      groups.push({
        tableId: state.focusTableId,
        tableName: state.focusTable.display_name || state.focusTable.table_name,
        tableAlias: null,
        isFocusTable: true,
        fields: focusFields,
      });
    }

    // Joined table fields - use columns from joinedTableColumns state
    // Filter out system columns, PK and FK columns
    state.selectedJoins.forEach((join) => {
      const columns = (joinedTableColumns[join.targetTableId] || []).filter(
        (col) =>
          !isSystemColumn(col.column_name) &&
          !col.is_primary_key &&
          !col.is_foreign_key,
      );

      const joinFields: AvailableField[] = columns.map((col) => ({
        column: col,
        tableId: join.targetTableId,
        tableName: join.targetTableName,
        tableAlias: join.alias,
        isSelected: isFieldSelected(col.id, join.alias),
      }));

      groups.push({
        tableId: join.targetTableId,
        tableName: join.targetTableName,
        tableAlias: join.alias,
        isFocusTable: false,
        fields: joinFields,
      });
    });

    return groups;
  }, [
    state.focusTable,
    state.focusTableId,
    state.selectedJoins,
    joinedTableColumns,
    isFieldSelected,
  ]);

  // Compute combined link field groups - group multiple link fields from the same joined table
  const combinedLinkFieldGroups = useMemo((): Map<
    string,
    CombinedLinkFieldGroup
  > => {
    const groups = new Map<string, CombinedLinkFieldGroup>();

    // Only look at joined table link fields (tableAlias is not null)
    const linkFields = state.selectedFields.filter(
      (f) => f.isLinkField && f.tableAlias !== null,
    );

    // Group by tableAlias
    const byAlias: Record<string, typeof linkFields> = {};
    linkFields.forEach((field) => {
      const alias = field.tableAlias!;
      if (!byAlias[alias]) {
        byAlias[alias] = [];
      }
      byAlias[alias].push(field);
    });

    // Create combined groups for aliases with 2+ link fields
    Object.entries(byAlias).forEach(([alias, fields]) => {
      if (fields.length >= 2) {
        // Sort by sortOrder to maintain consistent label
        const sortedFields = [...fields].sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );
        const combinedLabel = sortedFields
          .map((f) => f.displayLabel)
          .join(" + ");

        groups.set(alias, {
          tableAlias: alias,
          tableName: fields[0].tableName,
          fieldIds: sortedFields.map((f) => f.id),
          combinedLabel,
        });
      }
    });

    return groups;
  }, [state.selectedFields]);

  // Get the combined group for a field (returns undefined if not in a group)
  const getCombinedGroupForField = useCallback(
    (fieldId: string): CombinedLinkFieldGroup | undefined => {
      for (const group of combinedLinkFieldGroups.values()) {
        if (group.fieldIds.includes(fieldId)) {
          return group;
        }
      }
      return undefined;
    },
    [combinedLinkFieldGroups],
  );

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(state.selectedFields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    reorderFields(items);
  };

  const handleFieldToggle = (field: AvailableField) => {
    toggleField(field.column, field.tableId, field.tableName, field.tableAlias);
  };

  const handleDisplayLabelChange = (fieldId: string, label: string) => {
    updateField(fieldId, { displayLabel: label });
  };

  const handleVisibilityToggle = (fieldId: string, isVisible: boolean) => {
    updateField(fieldId, { isVisible });
  };

  const handleEditableToggle = (fieldId: string, isEditable: boolean) => {
    updateField(fieldId, { isEditable });
  };

  const handleLinkFieldToggle = (fieldId: string, isLinkField: boolean) => {
    updateField(fieldId, { isLinkField });
  };

  // At most one field per view identifies the signed-in user.
  const handleUserIdentifierToggle = (
    fieldId: string,
    isUserIdentifier: boolean,
  ) => {
    if (isUserIdentifier) {
      state.selectedFields
        .filter((other) => other.id !== fieldId && other.isUserIdentifier)
        .forEach((other) => updateField(other.id, { isUserIdentifier: false }));
    }
    updateField(fieldId, { isUserIdentifier });
  };

  const handleWidthChange = (
    fieldId: string,
    widthHint: number | undefined,
  ) => {
    updateField(fieldId, { widthHint });
  };

  const handleFormatChange = (
    fieldId: string,
    formatType: string | undefined,
  ) => {
    updateField(fieldId, { formatType });
  };

  const toggleSettings = (fieldId: string) => {
    setSettingsExpandedId((prev) => (prev === fieldId ? null : fieldId));
  };

  if (!state.focusTableId) {
    return (
      <Alert color="warning">
        Please select a focus table in Step 1 before selecting fields.
      </Alert>
    );
  }

  const totalAvailable = fieldGroups.reduce(
    (sum, g) => sum + g.fields.length,
    0,
  );
  const totalSelectedFromAvailable = fieldGroups.reduce(
    (sum, g) => sum + g.fields.filter((f) => f.isSelected).length,
    0,
  );
  const allSelected =
    totalAvailable > 0 && totalSelectedFromAvailable === totalAvailable;
  const someSelected =
    totalSelectedFromAvailable > 0 &&
    totalSelectedFromAvailable < totalAvailable;
  const selectedCount = state.selectedFields.length;
  const hasJoins = state.selectedJoins.length > 0;

  // Handler to select or deselect all available fields
  const handleSelectAll = (checked: boolean) => {
    fieldGroups.forEach((group) => {
      group.fields.forEach((field) => {
        // Only toggle if current state doesn't match desired state
        if (field.isSelected !== checked) {
          toggleField(
            field.column,
            field.tableId,
            field.tableName,
            field.tableAlias,
          );
        }
      });
    });
  };

  return (
    <Stack spacing={2}>
      {/* Validation Error */}
      {fieldsError && (
        <Alert color="danger" variant="soft">
          {fieldsError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 3,
          maxHeight: "calc(100vh - 400px)",
          minHeight: 200,
        }}
      >
        {/* Left Panel: Available Fields */}
        <Card
          variant="outlined"
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ pb: 1, flexGrow: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ViewColumnIcon color="primary" />
              <Typography level="title-lg">Available Fields</Typography>
              <Chip size="sm" variant="soft">
                {totalAvailable}
              </Chip>
              {loadingJoinedColumns && hasJoins && (
                <CircularProgress size="sm" />
              )}
              <Box sx={{ flex: 1 }} />
              <Tooltip title={allSelected ? "Deselect all" : "Select all"}>
                <span>
                  <Checkbox
                    size="sm"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    label="All"
                    sx={{ mr: 1 }}
                  />
                </span>
              </Tooltip>
            </Stack>
            <Typography
              level="body-xs"
              sx={{ color: "text.tertiary", mt: 0.5 }}
            >
              Click to add fields to your view
            </Typography>
          </CardContent>

          <Box sx={{ flex: 1, overflow: "auto", px: 2, pb: 2 }}>
            <AccordionGroup variant="plain" sx={{ gap: 1 }}>
              {fieldGroups.map((group) => (
                <Accordion
                  key={`${group.tableId}-${group.tableAlias || "focus"}`}
                  defaultExpanded
                >
                  <AccordionSummary
                    indicator={<ExpandMoreIcon />}
                    sx={{
                      fontWeight: "md",
                      bgcolor: group.isFocusTable ? "primary.50" : "neutral.50",
                      borderRadius: "md",
                      px: 1.5,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TableChartIcon
                        fontSize="small"
                        sx={{
                          color: group.isFocusTable
                            ? "primary.500"
                            : "neutral.500",
                        }}
                      />
                      <Typography level="body-sm" fontWeight="md">
                        {group.tableName}
                      </Typography>
                      {group.tableAlias && (
                        <Chip size="sm" variant="soft" color="neutral">
                          {group.tableAlias}
                        </Chip>
                      )}
                      {group.isFocusTable && (
                        <Chip size="sm" variant="soft" color="primary">
                          Focus
                        </Chip>
                      )}
                      <Chip size="sm" variant="outlined">
                        {group.fields.filter((f) => f.isSelected).length}/
                        {group.fields.length}
                      </Chip>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={0.5} sx={{ py: 1 }}>
                      {group.fields.map((field) => (
                        <Sheet
                          key={`${field.column.id}-${field.tableAlias || "focus"}`}
                          variant={field.isSelected ? "soft" : "plain"}
                          color={field.isSelected ? "primary" : "neutral"}
                          sx={{
                            p: 1,
                            borderRadius: "sm",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                            "&:hover": {
                              bgcolor: field.isSelected
                                ? "primary.100"
                                : "neutral.100",
                            },
                          }}
                          onClick={() => handleFieldToggle(field)}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Checkbox
                              size="sm"
                              checked={field.isSelected}
                              onChange={() => handleFieldToggle(field)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Typography level="body-sm" sx={{ flex: 1 }}>
                              {field.column.display_name ||
                                field.column.column_name}
                            </Typography>
                            <Chip size="sm" variant="soft" color="neutral">
                              {getUserFriendlyType(field.column.data_type)}
                            </Chip>
                          </Stack>
                        </Sheet>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionGroup>
          </Box>
        </Card>

        {/* Right Panel: Selected Fields */}
        <Card
          variant="outlined"
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ pb: 1, flexGrow: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ViewColumnIcon color="success" />
              <Typography level="title-lg">Selected Fields</Typography>
              <Chip size="sm" variant="soft" color="success">
                {selectedCount}
              </Chip>
            </Stack>
            <Typography
              level="body-xs"
              sx={{ color: "text.tertiary", mt: 0.5 }}
            >
              Drag to reorder • Click icons to configure
            </Typography>
          </CardContent>

          <Box sx={{ flex: 1, overflow: "auto", px: 2, pb: 2 }}>
            {state.selectedFields.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.tertiary",
                }}
              >
                <Typography level="body-sm">
                  No fields selected. Click fields on the left to add them.
                </Typography>
              </Box>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="selected-fields">
                  {(provided) => (
                    <Stack
                      spacing={1}
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      sx={{ py: 1 }}
                    >
                      {state.selectedFields.map((field, index) => {
                        const combinedGroup = getCombinedGroupForField(
                          field.id,
                        );
                        const isFirstInGroup =
                          combinedGroup &&
                          combinedGroup.fieldIds[0] === field.id;

                        return (
                          <Draggable
                            key={field.id}
                            draggableId={field.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Sheet
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  borderRadius: "md",
                                  bgcolor: snapshot.isDragging
                                    ? "primary.50"
                                    : combinedGroup
                                      ? "success.50"
                                      : "background.surface",
                                  boxShadow: snapshot.isDragging
                                    ? "md"
                                    : "none",
                                  transition: "box-shadow 0.2s ease",
                                  borderColor: combinedGroup
                                    ? "success.300"
                                    : undefined,
                                  borderLeft: combinedGroup
                                    ? "3px solid"
                                    : undefined,
                                  borderLeftColor: combinedGroup
                                    ? "success.500"
                                    : undefined,
                                }}
                              >
                                <Stack spacing={1}>
                                  {/* Combined Link Field Group Header - only show for first field in group */}
                                  {isFirstInGroup && combinedGroup && (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{
                                        pb: 1,
                                        mb: 1,
                                        borderBottom: "1px dashed",
                                        borderColor: "success.200",
                                      }}
                                    >
                                      <JoinInnerIcon
                                        sx={{
                                          fontSize: 16,
                                          color: "success.600",
                                        }}
                                      />
                                      <Typography
                                        level="body-xs"
                                        fontWeight="md"
                                        sx={{ color: "success.700" }}
                                      >
                                        Combined Link Field
                                      </Typography>
                                      <Chip
                                        size="sm"
                                        variant="soft"
                                        color="success"
                                      >
                                        {combinedGroup.combinedLabel}
                                      </Chip>
                                      <Tooltip title="Multiple link fields from the same joined table are combined into a single display column">
                                        <Typography
                                          level="body-xs"
                                          sx={{
                                            color: "success.500",
                                            cursor: "help",
                                          }}
                                        >
                                          ({combinedGroup.fieldIds.length}{" "}
                                          fields)
                                        </Typography>
                                      </Tooltip>
                                    </Stack>
                                  )}

                                  {/* Main Row */}
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Box
                                      {...provided.dragHandleProps}
                                      sx={{ cursor: "grab" }}
                                    >
                                      <DragIndicatorIcon
                                        sx={{
                                          color: "neutral.400",
                                          fontSize: 20,
                                        }}
                                      />
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      {editingFieldId === field.id ? (
                                        <Input
                                          size="sm"
                                          value={field.displayLabel}
                                          onChange={(e) =>
                                            handleDisplayLabelChange(
                                              field.id,
                                              e.target.value,
                                            )
                                          }
                                          onBlur={() => setEditingFieldId(null)}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter")
                                              setEditingFieldId(null);
                                          }}
                                          autoFocus
                                        />
                                      ) : (
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          alignItems="center"
                                        >
                                          <Typography
                                            level="body-sm"
                                            fontWeight="md"
                                            noWrap
                                          >
                                            {field.displayLabel}
                                          </Typography>
                                          <IconButton
                                            size="sm"
                                            variant="plain"
                                            color="neutral"
                                            onClick={() =>
                                              setEditingFieldId(field.id)
                                            }
                                          >
                                            <EditIcon sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        </Stack>
                                      )}
                                      <Typography
                                        level="body-xs"
                                        sx={{ color: "text.tertiary" }}
                                        noWrap
                                      >
                                        {field.tableAlias || field.tableName}.
                                        {field.column.column_name}
                                      </Typography>
                                    </Box>

                                    <Stack direction="row" spacing={0.5}>
                                      <Tooltip
                                        title={
                                          field.isVisible ? "Visible" : "Hidden"
                                        }
                                      >
                                        <IconButton
                                          size="sm"
                                          variant={
                                            field.isVisible ? "soft" : "plain"
                                          }
                                          color={
                                            field.isVisible
                                              ? "success"
                                              : "neutral"
                                          }
                                          onClick={() =>
                                            handleVisibilityToggle(
                                              field.id,
                                              !field.isVisible,
                                            )
                                          }
                                        >
                                          {field.isVisible ? (
                                            <VisibilityIcon
                                              sx={{ fontSize: 18 }}
                                            />
                                          ) : (
                                            <VisibilityOffIcon
                                              sx={{ fontSize: 18 }}
                                            />
                                          )}
                                        </IconButton>
                                      </Tooltip>

                                      {/* Only show editable toggle for focus table fields */}
                                      {field.tableAlias === null && (
                                        <Tooltip
                                          title={
                                            field.isEditable
                                              ? "Editable"
                                              : "Read-only"
                                          }
                                        >
                                          <IconButton
                                            size="sm"
                                            variant={
                                              field.isEditable
                                                ? "soft"
                                                : "plain"
                                            }
                                            color={
                                              field.isEditable
                                                ? "primary"
                                                : "neutral"
                                            }
                                            onClick={() =>
                                              handleEditableToggle(
                                                field.id,
                                                !field.isEditable,
                                              )
                                            }
                                            disabled={
                                              field.column.is_primary_key
                                            }
                                          >
                                            <EditIcon sx={{ fontSize: 18 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}

                                      {/* Link field toggle for FK columns */}
                                      {field.column.is_foreign_key && (
                                        <Tooltip
                                          title={
                                            field.isLinkField
                                              ? "Link field (opens record)"
                                              : "Regular field"
                                          }
                                        >
                                          <IconButton
                                            size="sm"
                                            variant={
                                              field.isLinkField
                                                ? "soft"
                                                : "plain"
                                            }
                                            color={
                                              field.isLinkField
                                                ? "success"
                                                : "neutral"
                                            }
                                            onClick={() =>
                                              handleLinkFieldToggle(
                                                field.id,
                                                !field.isLinkField,
                                              )
                                            }
                                          >
                                            <LinkIcon sx={{ fontSize: 18 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}

                                      {/* User identifier: makes the view personal */}
                                      <Tooltip
                                        title={
                                          field.isUserIdentifier
                                            ? "User identifier: members see only their own rows"
                                            : "Mark as the field that identifies the signed-in user"
                                        }
                                      >
                                        <IconButton
                                          size="sm"
                                          variant={
                                            field.isUserIdentifier
                                              ? "soft"
                                              : "plain"
                                          }
                                          color={
                                            field.isUserIdentifier
                                              ? "warning"
                                              : "neutral"
                                          }
                                          aria-label="User identifier"
                                          aria-pressed={field.isUserIdentifier}
                                          onClick={() =>
                                            handleUserIdentifierToggle(
                                              field.id,
                                              !field.isUserIdentifier,
                                            )
                                          }
                                        >
                                          <PersonPinIcon
                                            sx={{ fontSize: 18 }}
                                          />
                                        </IconButton>
                                      </Tooltip>

                                      {/* Settings toggle */}
                                      <Tooltip title="Width & Format settings">
                                        <IconButton
                                          size="sm"
                                          variant={
                                            settingsExpandedId === field.id
                                              ? "soft"
                                              : "plain"
                                          }
                                          color={
                                            settingsExpandedId === field.id
                                              ? "primary"
                                              : "neutral"
                                          }
                                          onClick={() =>
                                            toggleSettings(field.id)
                                          }
                                        >
                                          <TuneIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>

                                    <Chip
                                      size="sm"
                                      variant="soft"
                                      color="neutral"
                                    >
                                      {getUserFriendlyType(
                                        field.column.data_type,
                                      )}
                                    </Chip>
                                  </Stack>

                                  {/* Expandable Settings Row */}
                                  {settingsExpandedId === field.id && (
                                    <Stack
                                      direction="row"
                                      spacing={2}
                                      sx={{
                                        pt: 1.5,
                                        mt: 1,
                                        borderTop: "1px dashed",
                                        borderColor: "neutral.200",
                                      }}
                                    >
                                      <FormControl
                                        size="sm"
                                        sx={{ minWidth: 140 }}
                                      >
                                        <FormLabel sx={{ fontSize: "xs" }}>
                                          Width
                                        </FormLabel>
                                        <Select
                                          size="sm"
                                          value={
                                            field.widthHint?.toString() ??
                                            "auto"
                                          }
                                          onChange={(_e, value) =>
                                            handleWidthChange(
                                              field.id,
                                              value === "auto" || value === null
                                                ? undefined
                                                : parseInt(value, 10),
                                            )
                                          }
                                        >
                                          {WIDTH_OPTIONS.map((opt) => (
                                            <Option
                                              key={opt.label}
                                              value={
                                                opt.value?.toString() ?? "auto"
                                              }
                                            >
                                              {opt.label}
                                            </Option>
                                          ))}
                                        </Select>
                                      </FormControl>

                                      <FormControl
                                        size="sm"
                                        sx={{ minWidth: 180 }}
                                      >
                                        <FormLabel sx={{ fontSize: "xs" }}>
                                          Format
                                        </FormLabel>
                                        <Select
                                          size="sm"
                                          value={field.formatType ?? "default"}
                                          onChange={(_e, value) =>
                                            handleFormatChange(
                                              field.id,
                                              value === "default"
                                                ? undefined
                                                : (value as string),
                                            )
                                          }
                                        >
                                          {getFormatOptionsForType(
                                            field.column.data_type,
                                          ).map((opt) => (
                                            <Option
                                              key={opt.label}
                                              value={opt.value ?? "default"}
                                            >
                                              {opt.label}
                                            </Option>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Stack>
                                  )}
                                </Stack>
                              </Sheet>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Box>
        </Card>
      </Box>
    </Stack>
  );
}
